import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  DbKeyValues,
  formInputInfo,
  MailKeyValues,
  requiredType,
} from "../../types/types";
import { FormInput } from "../form/FormInput";
import { FormSelect } from "../form/FormSelect";
import {
  HiOutlineTicket,
  HiOutlineTrash,
  HiOutlineEyeOff,
} from "react-icons/hi";
import { BiPlus, BiMinus } from "react-icons/bi";
import { IoIosRepeat } from "react-icons/io";
import { FormCheckBox } from "../form/FormCheckBox";
import { IconWrapper } from "../utils/IconWrapper";
import { useForm } from "react-hook-form";

interface ModalProps {
  modalAction: (objectI: formInputInfo, basket: string) => void;
  modalCancel: (id: string) => void;
  modalOpen: (modalId: string) => void;
  modalId: string;
  alertId: string;
  dependencyId: string;
  productId: string;
  hideId: string;
  item: formInputInfo;
  basket: string;
  dbKeys: DbKeyValues[];
  mailKeys: MailKeyValues[];
  questionIds: string[];
}

export const FormModal: React.FC<ModalProps> = ({
  modalAction,
  modalCancel,
  modalOpen,
  modalId,
  alertId,
  dependencyId,
  item,
  basket,
  productId,
  hideId,
  dbKeys,
  questionIds,
  mailKeys,
}) => {
  const inputStyle =
    "font-semibold text-brand-dark_blue focus:outline-none focus:bg-yellow-200 focus:shadow-inner border border-gray-300 rounded-lg py-2 px-4 m-4 appearance-none leading-normal cursor-pointer";
  const labelStyle = "m-2 uppercase font-semibold text-lg text-brand-dark_blue";
  const containerStyle = "relative w-1/3";
  const requiredOptions: requiredType[] = ["", "email", "phone", "any"];
  const databaseKeyOptions = [""].concat(dbKeys.map((a) => a.fieldName));
  const mailKeyOptions = [""].concat(mailKeys.map((a) => a.fieldName));
  const [state, setState] = useState({
    ...item,
    id: item.id ? item.id : "",
    options: item.options ? item.options : [""],
    description: item.description ? item.description! : "",
    required: item.required
      ? item.required!
      : { key: "", type: "" as requiredType, value: "" },
    link: item.link ? item.link! : "",
    reservation: item.reservation ? item.reservation : false,
    person: item.person ? item.person : false,
    dbKeys: item.dbKeys
      ? item.dbKeys
      : { key: "", entryValue: "", leadKey: false, dbKey: false },
    mailKeys: item.mailKeys ? item.mailKeys : { key: "" },
  });
  const [message, setMessage] = useState<string>("");
  const hideOptions =
    state.type === "radio" ||
    state.type === "checkbox" ||
    state.type === "dropdown" ||
    state.type === "product"
      ? "visible"
      : "invisible";
  const confirMationButton = !message
    ? "bg-brand-white hover:bg-blue-200 text-brand-blue m-4 rounded w-20 h-10 border border-gray-200 shadow-lg"
    : "bg-red-300 text-brand-white m-4 rounded w-20 h-10";
  const { register, trigger, errors } = useForm();

  function validateWithUseForm(id: string) {
    trigger(id);
  }

  const localInputChange = (e: any, item?: string, itemAttribute?: string) => {
    if (itemAttribute) {
      setState({
        ...state,
        [item]: { ...state[item], [itemAttribute]: e.target.value },
      });
    } else if (e.target.id === "type" && e.target.value === "accept") {
      setState({
        ...state,
        options: [" "],
        [e.target.id]: e.target.value,
      });
    } else {
      setState({ ...state, [e.target.id]: e.target.value });
    }
  };

  const changeDbKey = (e: any) => {
    const obj = dbKeys.find((dbKey) => dbKey.fieldName === e.target.value);
    if (!obj) {
      setState({
        ...state,
        dbKeys: {
          key: "",
          entryValue: "",
          leadKey: false,
          dbKey: false,
        },
      });
    } else {
      const result = {
        key: obj.internalDbValue,
        ...(obj.entryValue && { entryValue: obj.entryValue }),
        leadKey: obj.leadKey ? obj.leadKey : false,
        dbKey: obj.dbKey ? obj.dbKey : false,
      };
      setState({
        ...state,
        dbKeys: result,
      });
    }
  };

  const changeMailKey = (e: any) => {
    const obj = mailKeys.find(
      (mailKey) => mailKey.fieldName === e.target.value
    );
    if (!obj) {
      setState({
        ...state,
        mailKeys: {
          key: "",
        },
      });
    } else {
      const result = {
        key: obj.internalDbValue,
        groupRequired: obj.groupRequired ? obj.groupRequired : false,
      };
      setState({
        ...state,
        mailKeys: result,
      });
    }
  };

  const arrayInputChange = (e: any, index: number) => {
    if (state.options) {
      const copy = [...state.options];
      copy[index] = e.target.value;
      setState({ ...state, options: copy });
    }
  };

  const addOptions = () => {
    let newArr: string[] = [];
    if (state.options) {
      newArr = [...state.options, ""];
    } else {
      newArr = [""];
    }
    setState({ ...state, options: newArr });
  };

  const setBoolean = (e: any, key: string) => {
    const result = state[key] ? false : true;
    setState({ ...state, [key]: result });
  };

  const removeOption = () => {
    if (state.options) {
      const newArr = [...state.options];
      newArr.pop();
      setState({ ...state, options: newArr });
    }
  };

  const useFormatMessage = (messageId: string) => {
    return useIntl().formatMessage({ id: messageId });
  };

  function validate(str: string) {
    if (state.type === "product") {
      const splitted = str.split(" ");
      if (
        splitted.length <= 1 ||
        isNaN(Number(splitted[splitted.length - 1].replace(",", ".")))
      ) {
        setMessage(
          "Product in wrong format, please use '{product_name} {price}'  for ex. 'One day ticket 150.00'"
        );
        return false;
      }
    }
    setMessage("");
    return true;
  }

  const getDbFieldValue = (key: string, type: string) => {
    if (key === "") {
      return "";
    }
    return type === "dbKeys"
      ? dbKeys.find((k) => k.internalDbValue === key).fieldName
      : mailKeys.find((k) => k.internalDbValue === key).fieldName;
  };

  function returnRequiredRule(register: any) {
    return register({
      validate: (value) =>
        !questionIds.find((qId) => qId === value) || `Form.unique`,
    });
  }

  return (
    <form
      className='flex flex-col justify-around absolute z-40 text-center border bg-white border-gray-200 rounded w-2/3 shadow-xl mt-10 p-4 left-28'
      onSubmit={() => modalAction(state, basket)}
    >
      <div className='flex justify-center flex-wrap'>
        <FormInput
          id='question'
          divStyle='w-1/2'
          question={useFormatMessage("FormModal.question")}
          value={state.question}
          type='text'
          handleInputChange={localInputChange}
          containerStyle={containerStyle}
        />
        <FormInput
          id='id'
          divStyle='w-1/2'
          question={useFormatMessage("FormModal.id")}
          value={state.id}
          type='text'
          handleInputChange={localInputChange}
          register={returnRequiredRule(register)}
          validate={validateWithUseForm}
          errors={errors}
          containerStyle={containerStyle}
        />
        <FormInput
          id='description'
          divStyle='w-1/2'
          question={useFormatMessage("FormModal.description")}
          value={state.description}
          type='text'
          handleInputChange={localInputChange}
          containerStyle={containerStyle}
        />
        <FormSelect
          id='type'
          divStyle='w-1/2'
          options={[
            "",
            "text",
            "number",
            "radio",
            "checkbox",
            "textarea",
            "dropdown",
            "product",
            "accept",
          ]}
          value={state.type}
          question={useFormatMessage("FormModal.type")}
          handleInputChange={localInputChange}
          containerStyle={containerStyle}
        />
        <FormInput
          id='group'
          divStyle='w-1/2'
          question={useFormatMessage("FormModal.group")}
          type='number'
          value={state.group}
          handleInputChange={localInputChange}
          containerStyle={containerStyle}
        />
        <FormSelect
          id='required'
          divStyle='w-1/2'
          question={useFormatMessage("FormModal.required_type")}
          options={requiredOptions}
          value={state.required.type}
          itemAttribute='type'
          handleInputChange={localInputChange}
          containerStyle={containerStyle}
        />
        <FormSelect
          id='dbKeys'
          divStyle='w-1/2'
          question={useFormatMessage("FormModal.database_key")}
          options={databaseKeyOptions}
          value={getDbFieldValue(state.dbKeys.key, "dbKeys")}
          handleInputChange={changeDbKey}
          containerStyle={"relative w-1/3"}
        />
        <FormSelect
          id='mailKeys'
          divStyle='w-1/2'
          question={useFormatMessage("FormModal.email_key")}
          options={mailKeyOptions}
          value={getDbFieldValue(state.mailKeys.key, "mailKeys")}
          handleInputChange={changeMailKey}
          containerStyle={"relative w-1/3"}
        />
        <FormInput
          id='link'
          divStyle='w-1/2'
          question={useFormatMessage("FormModal.link")}
          type='text'
          value={state.link}
          handleInputChange={localInputChange}
          containerStyle={"relative w-1/3"}
        />
        <FormCheckBox
          id='person'
          divStyle='w-1/4'
          question={useFormatMessage("FormModal.person_type")}
          handleInputChange={setBoolean}
          centerCheckBox={true}
          options={[" "]}
          containerStyle={"relative w-1/4"}
          checked={state.person ? [" "] : []}
        />
        <FormCheckBox
          id='reservation'
          divStyle='w-1/4'
          question={useFormatMessage("FormModal.reservation")}
          centerCheckBox={true}
          handleInputChange={setBoolean}
          options={[" "]}
          containerStyle={"relative w-1/4"}
          checked={state.reservation ? [" "] : []}
        />
        <IconWrapper styles='text-brand-blue hover:text-brand-dark_blue cursor-pointer m-2'>
          <IoIosRepeat size={20} onClick={() => modalOpen(dependencyId)} />
        </IconWrapper>
        <IconWrapper styles='text-brand-blue hover:text-brand-dark_blue cursor-pointer m-2'>
          <HiOutlineTicket size={20} onClick={() => modalOpen(productId)} />
        </IconWrapper>
        <IconWrapper styles='text-brand-blue hover:text-brand-dark_blue cursor-pointer m-2'>
          <HiOutlineEyeOff size={20} onClick={() => modalOpen(hideId)} />
        </IconWrapper>
        <IconWrapper styles='text-brand-blue hover:text-brand-dark_blue cursor-pointer m-2'>
          <HiOutlineTrash size={20} onClick={() => modalOpen(alertId)} />
        </IconWrapper>
      </div>

      <div className={`flex flex-wrap ${hideOptions}`}>
        {state.options?.map((option, index: number) => {
          const optionValue = state.options ? state.options[index] : "";
          return (
            <div key={index} className='w-1/4'>
              <label htmlFor={`option${index}`} className={labelStyle}>
                <FormattedMessage id='FormModal.option' />
              </label>
              <input
                name={`option${index}`}
                id={`option${index}`}
                value={optionValue}
                className={inputStyle}
                onChange={(e) => arrayInputChange(e, index)}
                onBlur={(e) => validate(e.target.value)}
              />
              {message ? <span className='text-red-300'>{message}</span> : null}
            </div>
          );
        })}

        <div>
          <IconWrapper styles='text-brand-blue hover:text-brand-dark_blue cursor-pointer'>
            <BiPlus size={20} onClick={addOptions} />
          </IconWrapper>
          <IconWrapper styles='text-brand-blue hover:text-brand-dark_blue cursor-pointer'>
            <BiMinus size={20} onClick={removeOption} />
          </IconWrapper>
        </div>
      </div>
      <div>
        <button
          onClick={() => modalAction(state, basket)}
          className={confirMationButton}
          disabled={message.length > 0}
        >
          <FormattedMessage id='FormModal.confirm_button' />
        </button>
        <button
          type='button'
          onClick={() => modalCancel(modalId)}
          className='bg-brand-white hover:bg-blue-200 text-brand-blue m-4 rounded w-20 h-10 border border-gray-200 shadow-lg'
        >
          <FormattedMessage id='FormModal.cancel_button' />
        </button>
      </div>
    </form>
  );
};

export default FormModal;
